import { CloseOutlined, RedoOutlined } from '@ant-design/icons';
import { Modal, Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';

import {
  useAddClientReturnMutation,
  useGetClientBalanceMutation,
} from '@app/api/ClientPayments';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import { useLazyGetPaymentAccountsForSystemQuery } from '@app/api/PaymentAccounts';
import { RefundWindowContent } from '@app/components/Chat/components/RefundWindow/components/RefundWindowContent.main';
import type { RefundFormValues } from '@app/components/Chat/components/RefundWindow/components/RefundWindowContent.main';
import {
  DATE_PICKER_FORMAT,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  PREFIX,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NClients } from '@app/types';
import { withTestId } from '@app/utils';

import '@app/components/Chat/components/RefundWindow/styles/RefundWindow.desktop.scss';

const { useForm } = Form;

type TRefundWindow = {
  client: NClients.ClientForView | null | undefined;
  sidebarInfo: {
    userName: string;
    avatar: string;
  };
  isClientForViewLoading: boolean;
  isRefundWindow: boolean;
  refetchHistory: () => Promise<void>;
  setIsRefundWindow: (newStatus: boolean) => void;
  setClientInfo: (clientInfo: NClients.ClientForView) => void;
};

export const RefundWindow = ({
  client,
  sidebarInfo: { userName, avatar: avatarSrc },
  isClientForViewLoading,
  isRefundWindow,
  refetchHistory,
  setIsRefundWindow,
  setClientInfo,
}: TRefundWindow) => {
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();

  const [form] = useForm<RefundFormValues>();

  const [paymentAccounts, setPaymentAccounts] = useState<ModelPaymentAccount[]>(
    []
  );

  const [addClientReturn] = useAddClientReturnMutation();
  const [getPaymentAccountsForSystem] =
    useLazyGetPaymentAccountsForSystemQuery();
  const [getClientBalance] = useGetClientBalanceMutation();

  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        const result = await getPaymentAccountsForSystem({
          ActiveBranchId: activeBranchId,
          branchId: activeBranchId,
          includeDeleted: false,
        }).unwrap();

        setPaymentAccounts(result);
      } catch (error) {
        showNotification(
          'При получении списка платежных аккаунтов произошла ошибка',
          FRONT_WARNING
        );
      }
    };

    fetchPaymentAccounts();
  }, [activeBranchId, getPaymentAccountsForSystem, showNotification]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setIsRefundWindow(false);
  }, [form, setIsRefundWindow]);

  const sendRefundToServer = useCallback(async () => {
    try {
      let formValues: RefundFormValues;

      try {
        formValues = await form.validateFields();
      } catch {
        return;
      }

      if (!activeBranchId) {
        return showNotification(
          'Идентификатор филиала не найден',
          FRONT_WARNING
        );
      }

      if (!client) {
        return showNotification('Данные клиента не найдены', FRONT_WARNING);
      }

      const { Id: clientId } = client;
      const { comment, paymentAccountId, sum, refundDate } = formValues;

      await addClientReturn({
        ActiveBranchId: activeBranchId,
        clientId,
        accountId: paymentAccountId,
        amount: sum,
        comment,
        returnDate: refundDate.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
      }).unwrap();

      form.resetFields();
      setIsRefundWindow(false);

      const { Balance } = await getClientBalance({
        clientId,
        ActiveBranchId: activeBranchId,
        data: [],
      }).unwrap();

      setClientInfo({
        ...client,
        Balance: Balance,
      });

      refetchHistory();

      showNotification('Возврат с баланса клиента совершен успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    activeBranchId,
    client,
    form,
    addClientReturn,
    setIsRefundWindow,
    getClientBalance,
    setClientInfo,
    refetchHistory,
    showNotification,
  ]);

  return (
    <Modal
      className={`${PREFIX}refund-from-balance-window`}
      title="Возврат с баланса клиента"
      open={isRefundWindow}
      destroyOnHidden
      loading={isClientForViewLoading}
      okText="Вернуть"
      cancelText="Отменить"
      okButtonProps={{
        icon: <RedoOutlined />,
        onClick: sendRefundToServer,
        'data-testid': 'refund-submit',
      }}
      cancelButtonProps={{
        onClick: handleCancel,
        'data-testid': 'refund-cancel',
      }}
      closeIcon={<CloseOutlined data-testid="refund-close" />}
      modalRender={withTestId('client-balance-refund-popup')}
      onCancel={handleCancel}
    >
      <RefundWindowContent
        client={client}
        form={form}
        userName={userName}
        avatarSrc={avatarSrc}
        paymentAccounts={paymentAccounts}
      />
    </Modal>
  );
};
